import { contentBlocksAdapter } from "@/services/contentful/assemblies/page/adapter/content-blocks.adapter";
import { getBlockId } from "@/services/contentful/assemblies/page/adapter/utils";
import {
  UiConfigurationAdapter,
  type UiConfigurationInput,
} from "@/services/contentful/topics/UIConfiguration/ui-configuration.adapter";
import type { PageBlock, TabContainerPageBlock } from "@/types/page-block";

type ContentfulColumnWidth = "narrow" | "medium" | "wide";

export interface TabContainerItemInput {
  sys?: { id: string };
  __typename: "TabContainerItemD2C";
  internalName: string;
  title: string;
  contentBlocksCollection?: {
    items: Array<{ __typename?: string } | null>;
  } | null;
}

export interface TabContainerInput {
  sys?: { id: string };
  __typename: "TabContainerD2C";
  internalName: string;
  title: string;
  showNavigationArrows?: boolean | null;
  columnWidth?: ContentfulColumnWidth | null;
  itemsCollection?: {
    items: Array<TabContainerItemInput | null>;
  } | null;
  uiConfiguration?: UiConfigurationInput;
}

const NARROW_COLUMN_WIDTH = 8;
const MEDIUM_COLUMN_WIDTH = 10;
const WIDE_COLUMN_WIDTH = 12;

const COLUMN_WIDTH_MAP = {
  narrow: NARROW_COLUMN_WIDTH,
  medium: MEDIUM_COLUMN_WIDTH,
  wide: WIDE_COLUMN_WIDTH,
} as const;

type TabContainerItemPageBlock = TabContainerPageBlock["items"][number];

function normalizeColumnWidth(
  value: TabContainerInput["columnWidth"]
): TabContainerPageBlock["columnWidth"] {
  const normalizedValue = value?.trim().toLowerCase() as
    | ContentfulColumnWidth
    | undefined;

  if (!normalizedValue) {
    return MEDIUM_COLUMN_WIDTH;
  }

  return COLUMN_WIDTH_MAP[normalizedValue] ?? MEDIUM_COLUMN_WIDTH;
}

function adaptTabContainerItem(
  item: TabContainerItemInput,
  index: number,
  locale?: string,
  basePath?: string
): TabContainerItemPageBlock {
  const rawChildren = item.contentBlocksCollection?.items ?? [];
  const children = contentBlocksAdapter(
    rawChildren as Parameters<typeof contentBlocksAdapter>[0],
    locale,
    basePath
  );

  return {
    sys: item.sys,
    id: getBlockId(item, index),
    __typename: "TabContainerItemD2C",
    internalName: item.internalName,
    title: item.title,
    children: children as PageBlock[],
  };
}

export function TabContainerAdapter(
  item: TabContainerInput,
  index: number,
  locale?: string,
  basePath?: string
): TabContainerPageBlock {
  const rawItems = item.itemsCollection?.items ?? [];

  const items = rawItems
    .filter((entry): entry is TabContainerItemInput => entry !== null)
    .map((entry, itemIndex) =>
      adaptTabContainerItem(entry, itemIndex, locale, basePath)
    );

  return {
    sys: item.sys,
    id: getBlockId(item, index),
    __typename: "TabContainerD2C",
    internalName: item.internalName,
    title: item.title,
    showNavigationArrows: item.showNavigationArrows ?? false,
    columnWidth: normalizeColumnWidth(item.columnWidth),
    items,
    uiConfiguration: item.uiConfiguration
      ? UiConfigurationAdapter(item.uiConfiguration)
      : undefined,
  };
}
